import { action } from '@ember/object';
import { waitFor } from '@ember/test-waiters';
import Component from '@glimmer/component';
import { restartableTask, timeout } from 'ember-concurrency';
import t from 'ember-intl/helpers/t';
import PowerSelect from 'ember-power-select/components/power-select';
import config from 'invite-sign/config/environment';

const RESULTS_COUNT = 5;

interface LocalityAutocompleteSignature {
  Args: {
    id: string;
    name: string;
    placeId?: string;
    type?: string;
    updateName: (name: string) => void;
    updatePlaceId: (placeId: string) => void;
  };
}

interface Option {
  id: string;
  name: string;
}

interface AutocompleteResponseItem {
  id: string;
  attributes: {
    description: string;
    'matched-substrings': { length: number; offset: number }[];
  };
}

export default class LocalityAutocomplete extends Component<LocalityAutocompleteSignature> {
  get selection() {
    const { placeId, name } = this.args;

    return placeId && name ? { id: placeId, name } : null;
  }

  get types() {
    return (this.args.type ?? '').split(',');
  }

  get placeholderText() {
    if (this.types.includes('address') || this.types.includes('geocode')) return 'enter_your_address';

    return 'choose_a_city';
  }

  #buildPlacesAutocompleteUrl(term: string, types: string[] = []) {
    const typesFilter = types?.map((type) => (type ? `&filter[types][]=${type}` : '')).join('') ?? '';

    return `${config.apiHost}/api/v3/places/autocomplete?filter[search]=${term}${typesFilter}`;
  }

  @action
  didSelect(option: Option) {
    if (option && option.name && option.id) {
      this.args.updateName(option.name);
      this.args.updatePlaceId(option.id);
    }
  }

  autocompleteTask = restartableTask(
    waitFor(async (term) => {
      if (!term) return [];

      await timeout(config.autocompleteDebounceMs);

      const url = this.#buildPlacesAutocompleteUrl(term, this.types);

      try {
        const response = await fetch(url);
        if (!response.ok) return [];
        const { data } = await response.json();
        const formattedResults = data
          .slice(0, RESULTS_COUNT)
          .map((place: AutocompleteResponseItem) => ({ id: place.id, name: place.attributes.description }));

        return formattedResults;
      } catch {
        return [];
      }
    }),
  );

  <template>
    <PowerSelect
      @placeholder={{t this.placeholderText}}
      @searchEnabled={{true}}
      @search={{this.autocompleteTask.perform}}
      @selected={{this.selection}}
      @onChange={{this.didSelect}}
      as |option|
    >
      <span data-private>{{option.name}}</span>
    </PowerSelect>
  </template>
}
